// src/pages/admin/AdminDashboard.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  useEffect(() => {
    api.get('/admin/dashboard').then(r => setData(r.data)).catch(() => {});
    api.get('/analytics/summary').then(r => setAnalytics(r.data)).catch(() => {});
  }, []);
  if (!data) return <div className="loading"><div className="spinner" /></div>;

  const { stats, recentTxns, recentUsers, notifications } = data;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div>
          <div className="section-tag">Admin</div>
          <h1 className="section-title">ADMIN <span style={{ color: 'var(--neon-orange)' }}>DASHBOARD</span></h1>
        </div>
        {notifications.length > 0 && (
          <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.7rem', color: 'var(--neon-pink)' }}>
            🔔 {notifications.length} pending alerts
          </div>
        )}
      </div>

      {/* STAT GRID */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '1rem', marginBottom: '2rem' }}>
        {[
          { l: 'Total Users', v: stats.totalUsers, c: 'var(--neon-green)', link: '/admin/users' },
          { l: 'KYC Pending', v: stats.pendingKYC, c: 'var(--neon-yellow)', link: '/admin/kyc' },
          { l: 'Active Listings', v: stats.activeListings, c: 'var(--neon-cyan)', link: '/admin/listings' },
          { l: 'Total Transactions', v: stats.totalTxns, c: 'var(--neon-orange)', link: '/admin/transactions' },
          { l: 'Trading Volume', v: `₹${(stats.totalVolume / 100000).toFixed(1)}L`, c: 'var(--neon-green)', link: '/admin/transactions' },
          { l: 'Credits Traded', v: `${(stats.totalCredits / 1000).toFixed(1)}K tCO₂e`, c: 'var(--neon-cyan)', link: '/admin/transactions' },
          { l: 'EPA Surrendered', v: `${stats.epaCredits} tCO₂e`, c: 'var(--neon-orange)', link: '/admin/transactions' },
          { l: 'Covered Sectors', v: stats.sectors_covered || 9, c: 'var(--neon-purple)', link: '/market/india' },
          { l: 'Emissions Logged', v: stats.totalEmissionLogs || 0, c: 'var(--neon-orange)', link: '/admin' },
          { l: 'Avg Carbon Score', v: stats.avgCarbonScore ? `${stats.avgCarbonScore}/100` : '—', c: 'var(--neon-cyan)', link: '/admin' },
          { l: 'Service Logs', v: stats.totalServiceLogs || 0, c: 'var(--neon-green)', link: '/admin' },
          { l: 'Vendor Inquiries', v: stats.totalVendorInquiries || 0, c: 'var(--neon-yellow)', link: '/admin' },
        ].map((s, i) => (
          <Link key={i} to={s.link} style={{ textDecoration: 'none' }}>
            <div className="card" style={{ borderTop: `2px solid ${s.c}`, transition: 'all 0.3s' }}>
              <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.58rem', letterSpacing: '2px', color: 'var(--muted)', textTransform: 'uppercase', marginBottom: '0.4rem' }}>{s.l}</div>
              <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '1.8rem', color: s.c, letterSpacing: '2px' }}>{s.v}</div>
            </div>
          </Link>
        ))}
      </div>

      {/* RECENT ALERTS */}
      {notifications.length > 0 && (
        <div className="card" style={{ marginBottom: '2rem', borderTop: '2px solid var(--neon-yellow)' }}>
          <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '1.2rem', letterSpacing: '2px', marginBottom: '1rem' }}>PENDING ALERTS</div>
          <div style={{ maxHeight: '200px', overflow: 'auto' }} className="thin-scroll">
            {notifications.slice(0, 10).map((n, i) => (
              <div key={i} style={{ padding: '0.6rem 0', borderBottom: '1px solid rgba(255,255,255,0.04)', fontFamily: 'Space Mono, monospace', fontSize: '0.7rem', display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <span style={{ color: 'var(--neon-yellow)', fontSize: '0.65rem', whiteSpace: 'nowrap' }}>{n.type.replace('_', ' ').toUpperCase()}</span>
                <span style={{ color: 'var(--muted)' }}>{n.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ANALYTICS PANEL */}
      {analytics && (
        <div className="card" style={{ marginBottom: '2rem', borderTop: '2px solid var(--neon-purple)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem' }}>
            <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '1.2rem', letterSpacing: '2px' }}>PRODUCT ANALYTICS</div>
            <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.65rem', color: 'var(--muted)' }}>Last 7 days · self-hosted</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
            {[
              { l: 'Total Events', v: analytics.total_events, c: 'var(--neon-purple)' },
              { l: 'Events Today', v: analytics.today, c: 'var(--neon-cyan)' },
              { l: 'Unique Event Types', v: Object.keys(analytics.by_event || {}).length, c: 'var(--neon-green)' },
            ].map(s => (
              <div key={s.l} style={{ background: 'var(--surface2)', padding: '0.8rem 1rem' }}>
                <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.55rem', letterSpacing: '2px', color: 'var(--muted)', textTransform: 'uppercase', marginBottom: '0.4rem' }}>{s.l}</div>
                <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '1.6rem', color: s.c, letterSpacing: '2px' }}>{s.v}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            {/* Events by type */}
            <div>
              <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.6rem', letterSpacing: '2px', color: 'var(--muted)', textTransform: 'uppercase', marginBottom: '0.6rem' }}>Events by type</div>
              {Object.entries(analytics.by_event || {}).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([evt, count]) => (
                <div key={evt} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                  <span style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.65rem', color: 'var(--muted)' }}>{evt}</span>
                  <span style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.65rem', color: 'var(--neon-cyan)' }}>{count}</span>
                </div>
              ))}
            </div>
            {/* Signups by persona */}
            <div>
              <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.6rem', letterSpacing: '2px', color: 'var(--muted)', textTransform: 'uppercase', marginBottom: '0.6rem' }}>Signups by persona</div>
              {Object.entries(analytics.signups_by_persona || {}).sort((a, b) => b[1] - a[1]).map(([persona, count]) => (
                <div key={persona} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                  <span style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.65rem', color: 'var(--muted)' }}>{persona || 'Unknown'}</span>
                  <span style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.65rem', color: 'var(--neon-green)' }}>{count}</span>
                </div>
              ))}
            </div>
          </div>
          {/* 7-day bar chart */}
          {analytics.last_7_days?.length > 0 && (
            <div style={{ marginTop: '1.5rem' }}>
              <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.6rem', letterSpacing: '2px', color: 'var(--muted)', textTransform: 'uppercase', marginBottom: '0.8rem' }}>Daily event volume (7 days)</div>
              <ResponsiveContainer width="100%" height={100}>
                <BarChart data={analytics.last_7_days} margin={{ top: 0, right: 0, left: -30, bottom: 0 }}>
                  <XAxis dataKey="date" tick={{ fontFamily: 'Space Mono, monospace', fontSize: 9, fill: 'var(--muted)' }} />
                  <YAxis tick={{ fontFamily: 'Space Mono, monospace', fontSize: 9, fill: 'var(--muted)' }} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: 'var(--surface2)', border: '1px solid var(--border)', fontFamily: 'Space Mono, monospace', fontSize: '0.65rem' }} />
                  <Bar dataKey="count" fill="var(--neon-purple)" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        {/* RECENT TRANSACTIONS */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '1.2rem', letterSpacing: '2px' }}>RECENT TRANSACTIONS</div>
            <Link to="/admin/transactions" style={{ color: 'var(--neon-cyan)', fontFamily: 'Space Mono, monospace', fontSize: '0.65rem', textDecoration: 'none' }}>ALL →</Link>
          </div>
          <table className="data-table">
            <thead><tr><th>Type</th><th>Qty</th><th>Amount</th><th>Status</th></tr></thead>
            <tbody>
              {recentTxns.map(t => (
                <tr key={t.id}>
                  <td><span className={`badge badge-${t.type === 'BUY' ? 'green' : 'cyan'}`}>{t.type}</span></td>
                  <td>{t.qty} tCO₂e</td>
                  <td style={{ color: 'var(--neon-green)' }}>{t.total ? `₹${t.total.toLocaleString()}` : '—'}</td>
                  <td><span className="badge badge-green">{t.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* RECENT USERS */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '1.2rem', letterSpacing: '2px' }}>RECENT USERS</div>
            <Link to="/admin/users" style={{ color: 'var(--neon-cyan)', fontFamily: 'Space Mono, monospace', fontSize: '0.65rem', textDecoration: 'none' }}>ALL →</Link>
          </div>
          <table className="data-table">
            <thead><tr><th>Name</th><th>Role</th><th>KYC</th></tr></thead>
            <tbody>
              {recentUsers.map(u => (
                <tr key={u.id}>
                  <td>
                    <div>{u.name}</div>
                    <div style={{ color: 'var(--muted)', fontSize: '0.65rem' }}>{u.email}</div>
                  </td>
                  <td><span className="badge badge-muted">{u.role}</span></td>
                  <td><span className={`badge badge-${u.kyc_status === 'verified' ? 'green' : u.kyc_status === 'under_review' ? 'cyan' : 'yellow'}`}>{u.kyc_status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
